import classnames from 'classnames';
import {
    Archive,
    Bell,
    BookOpen,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronsDown,
    ChevronsUp,
    Copy,
    Crosshair,
    Crown,
    EllipsisVertical,
    Expand,
    Eye,
    Flag,
    Gem,
    Globe,
    GripVertical,
    Grid3x3,
    Hammer,
    Heart,
    Info,
    Landmark,
    LayoutGrid,
    LoaderCircle,
    Lock,
    Logs,
    Mail,
    Mars,
    MessageSquare,
    Minimize,
    Minus,
    Network,
    Pause,
    Pencil,
    Play,
    Plus,
    Puzzle,
    Rocket,
    RotateCw,
    Rotate3d,
    Rows3,
    Save,
    Search,
    Settings,
    Share2,
    Shield,
    ShieldCheck,
    Skull,
    SlidersHorizontal,
    Square,
    Star,
    Sword,
    Swords,
    Target,
    Timer,
    Trash2,
    TriangleAlert,
    Trophy,
    User,
    UserPen,
    Venus,
    X,
    Zap,
    ZoomIn,
    ZoomOut,
    CircleChevronDown,
    CircleChevronUp,
    CircleChevronLeft,
    CircleChevronRight,
} from 'lucide-react';
import type { ComponentPropsWithoutRef } from 'react';
import styles from './Icon.module.scss';

const ICON_COMPONENTS = {
    accept: Check,
    apps: Grid3x3,
    'arrow-down': ChevronDown,
    'arrow-left': ChevronLeft,
    'arrow-right': ChevronRight,
    'arrow-up': ChevronUp,
    'double-arrow-down': ChevronsDown,
    'double-arrow-up': ChevronsUp,
    'circle-arrow-down': CircleChevronDown,
    'circle-arrow-left': CircleChevronLeft,
    'circle-arrow-right': CircleChevronRight,
    'circle-arrow-up': CircleChevronUp,
    bell: Bell,
    book: BookOpen,
    chat: MessageSquare,
    chest: Archive,
    collapse: Minimize,
    copy: Copy,
    crosshair: Crosshair,
    crown: Crown,
    crystal: Gem,
    decline: X,
    expand: Expand,
    eye: Eye,
    flag: Flag,
    'gender-female': Venus,
    'gender-male': Mars,
    grip: GripVertical,
    hammer: Hammer,
    heart: Heart,
    info: Info,
    layout: LayoutGrid,
    error: TriangleAlert,
    loading: LoaderCircle,
    lock: Lock,
    log: Logs,
    mail: Mail,
    'menu-vertical': EllipsisVertical,
    pencil: Pencil,
    minus: Minus,
    museum: Landmark,
    network: Network,
    pause: Pause,
    planet: Globe,
    play: Play,
    plus: Plus,
    profile: User,
    puzzle: Puzzle,
    reload: RotateCw,
    reshape: UserPen,
    rocket: Rocket,
    'rotate-3d': Rotate3d,
    rows: Rows3,
    save: Save,
    search: Search,
    settings: Settings,
    share: Share2,
    shield: Shield,
    'shield-check': ShieldCheck,
    skull: Skull,
    star: Star,
    stop: Square,
    sword: Sword,
    swords: Swords,
    target: Target,
    timer: Timer,
    trophy: Trophy,
    trash: Trash2,
    tune: SlidersHorizontal,
    zap: Zap,
    'zoom-in': ZoomIn,
    'zoom-out': ZoomOut,
} as const;

export type IconName = keyof typeof ICON_COMPONENTS;
export type IconSize = 'small' | 'large';
export type IconSpin = 'slow' | 'fast';

const SIZE_CLASSES: Record<IconSize, string | undefined> = {
    small: styles.small,
    large: styles.large,
};

const SPIN_CLASSES: Record<IconSpin, string | undefined> = {
    slow: styles.spinSlow,
    fast: styles.spinFast,
};

interface IconProps extends ComponentPropsWithoutRef<'svg'> {
    icon: IconName;
    /** The glyph's size: `small` 16px, `large` 32px, inside a 4px margin. */
    size?: IconSize;
    /** Turns the glyph, in degrees. */
    rotate?: number;
    /** Keeps the glyph turning, e.g. a loading spinner. */
    spin?: IconSpin;
    /** Names the icon for assistive technology and shows as its hover tooltip. Without it the icon is decorative and hidden from them. */
    tooltip?: string;
}

/**
 * One glyph from the shared set, drawn as an `<svg>` in the current text
 * color. It is not interactive: a clickable icon is a `Button` with an
 * `Icon` inside.
 */
export function Icon({
    icon,
    size = 'small',
    className,
    rotate = 0,
    spin,
    tooltip,
    style,
    ...props
}: IconProps): JSX.Element {
    const IconComponent = ICON_COMPONENTS[icon];
    const labelProps = tooltip === undefined
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': tooltip };

    return (
        <IconComponent
            className={classnames(styles.icon, SIZE_CLASSES[size], spin !== undefined && SPIN_CLASSES[spin], className)}
            strokeWidth={1.75}
            style={rotate === 0 ? style : { rotate: `${rotate}deg`, ...style }}
            {...labelProps}
            {...props}
        >
            {/* SVG <title> is the native hover tooltip for an <svg> (it ignores the `title` attribute). */}
            {tooltip !== undefined && <title>{tooltip}</title>}
        </IconComponent>
    );
}
